// See app/serializers/rest/status_serializer.rb

import type { ApiAccountJSON } from './accounts';
import type { ApiCustomEmojiJSON } from './custom_emoji';
import type { ApiMediaAttachmentJSON } from './media_attachments';
import type { ApiPollJSON } from './polls';
import type { ApiQuoteJSON, ApiQuotePolicyJSON } from './quotes';

// See app/modals/status.rb
export type StatusVisibility =
  | 'public'
  | 'unlisted'
  | 'private'
  // | 'limited' // This is never exposed to the API (they become `private`)
  | 'direct'
  // Krew is an orthogonal, additive audience axis carried by `krews` — no
  // longer a visibility value (docs/decisions.md).
  // Kronk reach ladder (docs/spaces/feed.md (The reach ladder)) — local-only:
  //   mates     — the author's mutual connections
  //   orbit     — mates of mates (one hop out)
  //   self_only — the author's own timeline; radiates to no one
  | 'mates'
  | 'orbit'
  | 'self_only';

export interface ApiStatusApplicationJSON {
  name: string;
  website: string;
}

export interface ApiTagJSON {
  name: string;
  url: string;
}

export interface ApiMentionJSON {
  id: string;
  username: string;
  url: string;
  acct: string;
}

export interface ApiPreviewCardAuthorJSON {
  name: string;
  url: string;
  account?: ApiAccountJSON;
}

export interface ApiPreviewCardJSON {
  url: string;
  title: string;
  description: string;
  language: string;
  type: string;
  author_name: string;
  author_url: string;
  author_account?: ApiAccountJSON;
  provider_name: string;
  provider_url: string;
  html: string;
  width: number;
  height: number;
  image: string;
  image_description: string;
  embed_url: string;
  blurhash: string;
  published_at: string;
  authors: ApiPreviewCardAuthorJSON[];
}

export type FilterContext =
  | 'home'
  | 'notifications'
  | 'public'
  | 'thread'
  | 'account';

export interface ApiFilterJSON {
  id: string;
  title: string;
  context: FilterContext;
  expires_at: string;
  filter_action: 'warn' | 'hide';
  keywords?: unknown[]; // TODO: FilterKeywordSerializer
  statuses?: unknown[]; // TODO: FilterStatusSerializer
}

export interface ApiFilterResultJSON {
  filter: ApiFilterJSON;
  keyword_matches: string[];
  status_matches: string[];
}

export interface ApiStatusJSON {
  id: string;
  created_at: string;
  in_reply_to_id?: string;
  in_reply_to_account_id?: string;
  sensitive: boolean;
  spoiler_text?: string;
  visibility: StatusVisibility;
  language: string;
  uri: string;
  url: string;
  replies_count: number;
  reblogs_count: number;
  favorites_count: number;
  quotes_count: number;
  edited_at?: string;

  favorited?: boolean;
  reblogged?: boolean;
  muted?: boolean;
  bookmarked?: boolean;
  pinned?: boolean;

  filtered?: ApiFilterResultJSON[];
  content?: string;
  text?: string;

  reblog?: ApiStatusJSON;
  application?: ApiStatusApplicationJSON;
  account: ApiAccountJSON;
  media_attachments: ApiMediaAttachmentJSON[];
  mentions: ApiMentionJSON[];

  tags: ApiTagJSON[];
  emojis: ApiCustomEmojiJSON[];

  card?: ApiPreviewCardJSON;
  poll?: ApiPollJSON;
  quote?: ApiQuoteJSON;
  quote_approval?: ApiQuotePolicyJSON;

  // Krews this status is targeted at (the additive audience axis). Rich
  // references so the timeline badge can render the Krew name inline. Empty
  // array for a status that targets no krews.
  krews?: { id: string; slug: string; name: string }[];

  post_type?: 'normal' | 'question' | 'answer' | 'proposal';
  // Feed projection discriminator (docs/spaces/feed.md (How korners reach the feed)): the
  // korner slug this Status projects a card for; null for an ordinary post.
  source_korner?: string | null;
  question?: ApiStatusJSON;
  answers_count?: number;
  answerers?: { id: string; username: string; acct: string; avatar: string }[];
  has_answered?: boolean;

  proposal?: ApiProposalSummaryJSON;

  event?: {
    id: string;
    title: string;
    description: string;
    start_time: string;
    end_time: string | null;
    location_name: string | null;
    location_url: string | null;
    event_type: string;
    huddle_url: string | null;
    rsvp_enabled: boolean;
    max_attendees: number | null;
    going_count: number;
    interested_count: number;
    image_url: string | null;
    rsvp?: string | null;
    is_owner?: boolean;
  };

  // Map — a published Trek projected onto its timeline Status (feed projection,
  // docs/spaces/feed.md (How korners reach the feed)). `route` is the already privacy-trimmed
  // slice ([lng, lat] pairs) for the card's glimpse; null when the trek has no
  // route. See REST::TrekSummarySerializer.
  trek?: {
    id: string;
    activity_type: 'run' | 'walk' | 'hike' | 'swim' | 'ride' | 'paddle';
    title: string;
    distance_m: number;
    moving_sec: number;
    pace_seconds: number | null;
    speed_kmh: number | null;
    elevation_gain: number | null;
    recorded_at: string;
    has_route: boolean;
    route: [number, number][] | null;
  };
}

export interface ApiContextJSON {
  ancestors: ApiStatusJSON[];
  descendants: ApiStatusJSON[];
}

export interface ApiStatusSourceJSON {
  id: string;
  text: string;
  spoiler_text: string;
}

export function isStatusVisibility(
  visibility: string,
): visibility is StatusVisibility {
  return [
    'public',
    'unlisted',
    'private',
    'direct',
    'mates',
    'orbit',
    'self_only',
  ].includes(visibility);
}

// The Kommons proposal summary projected onto a Status (feed card + detail).
// Note: the backend serialiser emits `challenge_count` (not `veto_count` — that
// was the retired veto vocabulary), so the field name here must match.
export interface ApiProposalSummaryJSON {
  id: string;
  title: string;
  summary: string | null;
  status: 'open' | 'delivered' | 'completed' | 'annulled';
  proposal_type: 'small' | 'medium' | 'large';
  support_count: number;
  challenge_count: number;
  participation_count: number;
  categories: string[];
  created_at: string;
}

// "Who can see this?" readout — GET /api/v1/statuses/:id/audience (owner-only).
// See app/controllers/api/v1/statuses/audiences_controller.rb + docs/spaces/feed.md (Per-post audience)
export interface ApiStatusAudienceKrewJSON {
  id: string;
  name: string;
  slug: string;
}

export interface ApiStatusAudienceJSON {
  visibility: StatusVisibility;
  mates_count: number | null;
  krews: ApiStatusAudienceKrewJSON[];
  added: ApiAccountJSON[];
  removed: ApiAccountJSON[];
}
